/**
 * fieldMapper.js — map vision-AI extracted JSON (flat dotted/bracketed paths)
 * onto a form's defaultData shape, with type coercion and option matching.
 *
 * Space returns: { "inspectionAddress.postCode": "SW1A 1AA",
 *                  "appliances[0].make": "Worcester", ..., nulls for misses }
 * Target shape:  { inspectionAddress: { postCode: 'SW1A 1AA' },
 *                  appliances: [ { make: 'Worcester', ...defaults } ] }
 *
 * The mapping is ALWAYS conservative:
 *  - starts from structuredClone(defaultData) so defaults are preserved
 *  - only overwrites when the extracted value is non-null and coerces cleanly
 *  - select fields only accept a listed option (YES/NO aware), else keep default
 *  - numeric fields only accept finite numbers (units stripped), else keep default
 *  - everything applied is tracked in `appliedPaths` so the review UI can
 *    highlight exactly what the AI wrote
 */

/** Deep-clone JSON-safe data. */
function clone(data) {
  return typeof structuredClone === 'function'
    ? structuredClone(data)
    : JSON.parse(JSON.stringify(data));
}

const YES_WORDS = new Set(['yes', 'y', 'true', '✔', '✓']);
const NO_WORDS = new Set(['no', 'n', 'false', '✗', 'x']);

/** Coerce an extracted value to the type implied by a schema field. */
function coerce(value, field) {
  if (value === null || value === undefined) return { ok: false, value: undefined };
  const raw = typeof value === 'string' ? value.trim() : value;
  if (raw === '') return { ok: false, value: undefined };

  switch (field?.type) {
    case 'number': {
      const numeric = typeof raw === 'number'
        ? raw
        : Number(String(raw).replace(/[^0-9.-]/g, ''));
      if (!Number.isFinite(numeric)) return { ok: false, value: undefined };
      return { ok: true, value: numeric };
    }
    case 'select': {
      if (!field.options || !field.options.length) return { ok: true, value: String(raw) };
      const normalized = String(raw).toLowerCase().trim();
      const exact = field.options.find((o) => String(o).toLowerCase().trim() === normalized);
      if (exact !== undefined) return { ok: true, value: exact };
      if (field.options.length === 2) {
        const pair = field.options.map((o) => String(o).toLowerCase());
        if (pair.includes('yes') && pair.includes('no')) {
          if (YES_WORDS.has(normalized)) return { ok: true, value: field.options[pair.indexOf('yes')] };
          if (NO_WORDS.has(normalized)) return { ok: true, value: field.options[pair.indexOf('no')] };
        }
      }
      return { ok: false, value: undefined }; // unmatched — keep default, engineer picks
    }
    case 'tel':
      return { ok: true, value: String(raw).replace(/[^\d+()\- ]/g, '') };
    default:
      return { ok: true, value: String(raw) };
  }
}

/** Set a nested value; returns false when path is malformed/absent. */
function setPath(root, pathArr, value) {
  let node = root;
  for (let i = 0; i < pathArr.length - 1; i++) {
    const next = node?.[pathArr[i]];
    if (next === undefined || next === null) return false;
    node = next;
  }
  const leaf = pathArr[pathArr.length - 1];
  if (!node || typeof node !== 'object' || !(leaf in node)) return false;
  node[leaf] = value;
  return true;
}

/**
 * Parse "section.field" or "section[2].field" into structure.
 */
export function parsePath(path) {
  const repeatableMatch = /^([\w-]+)\[(\d+)\]\.([\w-]+)$/.exec(path);
  if (repeatableMatch) {
    return {
      isRepeatable: true,
      pathArr: [repeatableMatch[1], Number(repeatableMatch[2]), repeatableMatch[3]],
      nonRepeatablePath: `${repeatableMatch[1]}.${repeatableMatch[3]}`,
    };
  }
  const simpleMatch = /^([\w-]+)\.([\w-]+)$/.exec(path);
  if (!simpleMatch) return null;
  return {
    isRepeatable: false,
    pathArr: [simpleMatch[1], simpleMatch[2]],
    nonRepeatablePath: path,
  };
}

/**
 * Map extracted flat-path fields onto a defaultData-shaped payload.
 * @param {Object} defaultData form module defaultData
 * @param {Object} schema form module schema ({sections:[...]})
 * @param {Object} extracted flat-path map from the vision service
 * @returns {{formData: Object, appliedPaths: string[], skippedPaths: Array<{path:string,reason:string}>}}
 */
export function mapExtractedToFormData(defaultData, schema, extracted) {
  const formData = clone(defaultData || {});
  const appliedPaths = [];
  const skipped = [];
  if (!extracted || typeof extracted !== 'object') {
    return { formData, appliedPaths, skippedPaths: skipped };
  }

  // Index schema fields by their full path for coercion rules.
  const fieldByPath = new Map();
  for (const section of schema?.sections || []) {
    if (section.repeatable) continue;
    for (const field of section.fields || []) {
      if (!field.id) continue;
      fieldByPath.set(`${section.id}.${field.id}`, field);
    }
  }

  for (const [path, value] of Object.entries(extracted)) {
    const parsed = parsePath(path);
    if (!parsed) {
      skipped.push({ path, reason: 'unparseable path' });
      continue;
    }
    if (parsed.isRepeatable) {
      // Grow the repeatable array as needed; reuse the section's template
      // (first item of defaultData) as the base for extra items.
      const section = (schema?.sections || []).find((s) => s.id === parsed.pathArr[0]);
      if (!section || !Array.isArray(formData[parsed.pathArr[0]])) {
        skipped.push({ path, reason: 'repeatable section not in defaultData' });
        continue;
      }
      const arr = formData[parsed.pathArr[0]];
      const template = arr[0] ? clone(arr[0]) : null;
      while (arr.length <= parsed.pathArr[1]) {
        if (!template) {
          skipped.push({ path, reason: 'no repeatable template item' });
          break;
        }
        arr.push(clone(template));
      }
      const item = arr[parsed.pathArr[1]];
      const field = section.fields?.find((f) => f.id === parsed.pathArr[2]);
      const result = coerce(value, field);
      if (item && field && result.ok) {
        item[parsed.pathArr[2]] = result.value;
        appliedPaths.push(path);
      } else {
        skipped.push({ path, reason: result.ok ? 'field not in section' : 'value failed coercion' });
      }
      continue;
    }
    const field = fieldByPath.get(parsed.nonRepeatablePath);
    const result = coerce(value, field);
    if (!result.ok) {
      skipped.push({ path, reason: 'value failed coercion' });
      continue;
    }
    if (setPath(formData, parsed.pathArr, result.value)) {
      appliedPaths.push(path);
    } else {
      skipped.push({ path, reason: 'path not present in defaultData' });
    }
  }

  return { formData, appliedPaths, skippedPaths: skipped };
}
